import { useAuth } from '@/lib/auth/use-auth.js';
import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import { useState } from 'react';
import { useDeleteSavedView } from '../hooks/use-delete-saved-view.js';
import { useSavedViews } from '../hooks/use-saved-views.js';
import { useUpdateSavedView } from '../hooks/use-update-saved-view.js';
import type { SavedView, SavedViewBody } from '../lib/saved-views-client.js';
import { SharedViewBadge } from './shared-view-badge.js';

interface ManageViewsDialogProps {
  open: boolean;
  onClose: () => void;
  tenantReportId: string;
  canSaveSharedView: boolean;
}

interface RenameState {
  viewId: string;
  value: string;
  error?: string;
}

export function ManageViewsDialog({
  open,
  onClose,
  tenantReportId,
  canSaveSharedView,
}: ManageViewsDialogProps) {
  const { user } = useAuth();
  const currentUserId = user?.id ?? '';
  const isAdmin = user?.isAdmin === true;

  const { data } = useSavedViews(tenantReportId);
  const views = data?.items ?? [];

  const updateMutation = useUpdateSavedView({ tenantReportId });
  const deleteMutation = useDeleteSavedView({ tenantReportId });

  const [renameState, setRenameState] = useState<RenameState | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ viewId: string; message: string } | null>(null);

  if (!open) return null;

  const personal = views.filter((v) => v.scope === 'personal');
  const shared = views.filter((v) => v.scope === 'shared');

  function canActOnView(view: SavedView): boolean {
    if (view.scope === 'personal') return view.owner?.id === currentUserId;
    // shared: owner or admin can rename/delete
    return view.owner?.id === currentUserId || isAdmin;
  }

  function canShareView(view: SavedView): boolean {
    const isOwner = view.owner?.id === currentUserId;
    // Owner can always unshare (shared → personal) without extra permission.
    // Sharing (personal → shared) requires canSaveSharedView.
    if (!isOwner) return false;
    if (view.scope === 'shared') return true;
    return canSaveSharedView;
  }

  async function handleRenameSubmit(view: SavedView) {
    if (renameState === null) return;
    const trimmed = renameState.value.trim();
    if (trimmed.length === 0 || trimmed === view.name) {
      setRenameState(null);
      return;
    }
    const body: SavedViewBody = {
      name: trimmed,
      scope: view.scope,
      state: view.state,
    };
    try {
      await updateMutation.mutateAsync({ viewId: view.id, body });
      setRenameState(null);
      setRowError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al renombrar la vista.';
      setRenameState((prev) => (prev !== null ? { ...prev, error: message } : prev));
    }
  }

  async function handleToggleScope(view: SavedView) {
    const newScope = view.scope === 'personal' ? 'shared' : 'personal';
    const body: SavedViewBody = {
      name: view.name,
      scope: newScope,
      state: view.state,
    };
    try {
      await updateMutation.mutateAsync({ viewId: view.id, body });
      setRowError(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Error al cambiar el ámbito de la vista.';
      setRowError({ viewId: view.id, message });
    }
  }

  async function handleDelete(viewId: string) {
    await deleteMutation.mutateAsync(viewId);
    setDeleteConfirmId(null);
  }

  function renderRow(view: SavedView) {
    const isRenaming = renameState?.viewId === view.id;
    const isConfirmingDelete = deleteConfirmId === view.id;
    const canAct = canActOnView(view);
    const canShare = canShareView(view);
    const thisRowError = rowError?.viewId === view.id ? rowError.message : null;

    return (
      <tr
        key={view.id}
        className="border-t border-[var(--color-qup-border-dark)] text-sm text-[var(--color-qup-text-dark)]"
      >
        {/* Name */}
        <td className="px-3 py-2">
          {isRenaming ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleRenameSubmit(view);
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                value={renameState?.value ?? ''}
                // biome-ignore lint/a11y/noAutofocus: intentional UX — focus inline rename field immediately when editing starts
                autoFocus
                onChange={(e) => setRenameState({ viewId: view.id, value: e.target.value })}
                className={cn(
                  'rounded-[var(--radius-button)] border border-[var(--color-qup-border-dark)]',
                  'bg-[var(--color-qup-bg-dark)] px-2 py-1 text-xs',
                  'focus:outline-none focus:ring-1 focus:ring-[var(--color-qup-primary)]',
                )}
              />
              <button
                type="submit"
                className="text-xs text-[var(--color-qup-primary)] hover:underline focus-visible:outline-none"
              >
                OK
              </button>
              <button
                type="button"
                onClick={() => setRenameState(null)}
                className="text-xs text-[var(--color-qup-text-muted-dark)] hover:underline focus-visible:outline-none"
              >
                ✕
              </button>
              {renameState?.error !== undefined && (
                <span role="alert" className="text-xs text-[var(--color-qup-primary)]">
                  {renameState.error}
                </span>
              )}
            </form>
          ) : (
            <span>{view.name}</span>
          )}
        </td>

        {/* Scope */}
        <td className="px-3 py-2">
          {view.scope === 'shared' ? (
            <SharedViewBadge />
          ) : (
            <span className="text-xs text-[var(--color-qup-text-muted-dark)]">
              {t('saved_views.scope_personal')}
            </span>
          )}
        </td>

        {/* Owner */}
        <td className="px-3 py-2 text-xs text-[var(--color-qup-text-muted-dark)]">
          {view.owner?.displayName ?? '—'}
        </td>

        {/* Updated */}
        <td className="px-3 py-2 text-xs text-[var(--color-qup-text-muted-dark)]">
          {new Date(view.updatedAt).toLocaleDateString('es-ES')}
        </td>

        {/* Actions */}
        <td className="px-3 py-2">
          {isConfirmingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-qup-text-muted-dark)]">
                {t('saved_views.delete_confirm')}
              </span>
              <button
                type="button"
                onClick={() => void handleDelete(view.id)}
                className="text-xs text-[var(--color-qup-primary)] hover:underline focus-visible:outline-none"
              >
                Sí
              </button>
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="text-xs text-[var(--color-qup-text-muted-dark)] hover:underline focus-visible:outline-none"
              >
                No
              </button>
            </div>
          ) : canAct ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setRenameState({ viewId: view.id, value: view.name })}
                className="text-xs text-[var(--color-qup-text-dark)] hover:underline focus-visible:outline-none"
              >
                {t('saved_views.action_rename')}
              </button>
              {canShare && (
                <button
                  type="button"
                  onClick={() => void handleToggleScope(view)}
                  className="text-xs text-[var(--color-qup-text-dark)] hover:underline focus-visible:outline-none"
                >
                  {view.scope === 'personal'
                    ? t('saved_views.action_share')
                    : t('saved_views.action_unshare')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setDeleteConfirmId(view.id)}
                className="text-xs text-[var(--color-qup-primary)] hover:underline focus-visible:outline-none"
              >
                {t('saved_views.action_delete')}
              </button>
              {thisRowError !== null && (
                <span role="alert" className="text-xs text-[var(--color-qup-primary)]">
                  {thisRowError}
                </span>
              )}
            </div>
          ) : null}
        </td>
      </tr>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — button for keyboard-accessible dismiss */}
      <button
        type="button"
        className="absolute inset-0 bg-black/60 cursor-default"
        aria-label="Dismiss dialog"
        onClick={onClose}
      />

      {/* Panel */}
      {/* biome-ignore lint/a11y/useSemanticElements: stacked within a fixed backdrop; native <dialog> would require showModal() imperative API which conflicts with React state */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-views-dialog-title"
        className={cn(
          'relative w-full max-w-3xl rounded-[var(--radius-card)]',
          'border border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-card-dark)]',
          'shadow-[var(--shadow-card)] p-6',
        )}
      >
        <h2
          id="manage-views-dialog-title"
          className="mb-4 text-base font-semibold text-[var(--color-qup-text-dark)]"
        >
          {t('saved_views.dialog_manage_title')}
        </h2>

        {views.length === 0 ? (
          <p className="py-6 text-center text-sm text-[var(--color-qup-text-muted-dark)]">
            {t('saved_views.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wider text-[var(--color-qup-text-muted-dark)]">
                  <th className="px-3 py-2">{t('saved_views.field_name')}</th>
                  <th className="px-3 py-2">{t('saved_views.field_scope')}</th>
                  <th className="px-3 py-2">{t('saved_views.owner')}</th>
                  <th className="px-3 py-2">{t('saved_views.updated')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {personal.map(renderRow)}
                {shared.map(renderRow)}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'rounded-[var(--radius-button)] border border-[var(--color-qup-border-dark)]',
              'px-4 py-2 text-sm font-medium text-[var(--color-qup-text-dark)]',
              'hover:bg-[var(--color-qup-bg-dark)] transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
            )}
          >
            {t('saved_views.dialog_close')}
          </button>
        </div>
      </div>
    </div>
  );
}
